/**
 * --------------------------------------------------------------------------
 * CoreUI PRO scheduler render.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 * DOM layer over the core view-models: the time grid (day/week/resource),
 * the month grid, and the agenda list. Every meaningful element carries a
 * `data-part` attribute — the selector contract shared by all bindings and
 * the behavioral suite. User-provided strings only ever land in textContent.
 */

import type {
  DayOccurrences,
  EventOccurrence,
  InteractionState,
  MonthModel,
  OccurrenceBox,
  SchedulerLabels,
  SchedulerResource,
  SchedulerView,
  SchedulerViewModel,
  TimelineModel
} from '@coreui/scheduler-core'
import { MINUTES_IN_DAY } from '@coreui/scheduler-core'
import type { Temporal } from 'temporal-polyfill'
import {
  elementScroll,
  observeElementOffset,
  observeElementRect,
  Virtualizer
} from '@tanstack/virtual-core'

export interface BusinessHours {
  daysOfWeek?: number[]
  startHour: number
  endHour: number
  /** Reject moves/resizes/creates outside the business hours (default: display only). */
  enforce?: boolean
}

export interface RenderContext {
  businessHours: BusinessHours | null
  dayEndHour: number
  dayStartHour: number
  dayList: DayOccurrences[] | null
  dialogOccurrence: EventOccurrence | null
  hourHeight: number
  labels: SchedulerLabels
  locale?: string
  monthMaxEventsPerDay: number
  monthModel: MonthModel | null
  now: Temporal.ZonedDateTime | null
  recurringEditScope: string
  resourceColumnWidth: number
  resources: SchedulerResource[]
  selection: string[]
  showResourceButton: boolean
  timeZone: string
  timelineHourWidth: number
  timelineModel: TimelineModel | null
  view: SchedulerView
  viewModel: SchedulerViewModel | null
}

export interface BoxRef {
  box: OccurrenceBox
  columnIndex: number
  element: HTMLElement
}

export interface RendererCallbacks {
  onNavigate: (direction: 'prev' | 'next' | 'today') => void
  onViewSelect: (view: SchedulerView) => void
  onWindowChange?: () => void
  onMoreLink?: (dateISO: string) => void
}

// Below this the whole resource list renders directly; above it the columns
// window over a horizontal virtualizer.
const VIRTUALIZE_MIN_COLUMNS = 12

const isVirtualized = (ctx: RenderContext): boolean =>
  ctx.view === 'resource' && (ctx.viewModel?.columns.length ?? 0) > VIRTUALIZE_MIN_COLUMNS

const isTimeGridView = (view: SchedulerView): boolean =>
  view === 'day' || view === 'week' || view === 'resource'

const TIMELINE_LANE_HEIGHT = 36
const MONTH_BAR_HEIGHT = 22

const formatMinutes = (minutes: number) => {
  const clamped = Math.min(minutes, MINUTES_IN_DAY - 1)
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`
}

const toUTCDate = (date: Temporal.PlainDate) => new Date(Date.UTC(date.year, date.month - 1, date.day))

const el = (tag: string, className: string, part?: string): HTMLElement => {
  const node = document.createElement(tag)
  node.className = className

  if (part) {
    node.dataset.part = part
  }

  return node as HTMLElement
}

const localDateTimeValue = (value: Temporal.ZonedDateTime): string =>
  `${value.toPlainDate().toString()}T${String(value.hour).padStart(2, '0')}:${String(value.minute).padStart(2, '0')}`

const eventColor = (occurrence: EventOccurrence, resources: SchedulerResource[]): string | undefined =>
  occurrence.event.color ??
  resources.find(resource => resource.id === occurrence.event.resourceId)?.color

export function createRenderer(element: HTMLElement, callbacks: RendererCallbacks) {
  element.classList.add('scheduler')
  element.dataset.part = 'root'

  const toolbar = el('div', 'scheduler-toolbar', 'toolbar')
  const nav = el('div', 'scheduler-toolbar-nav')
  const prevButton = el('button', 'scheduler-nav-button', 'nav-prev') as HTMLButtonElement
  const todayButton = el('button', 'scheduler-nav-button scheduler-nav-today', 'nav-today') as HTMLButtonElement
  const nextButton = el('button', 'scheduler-nav-button', 'nav-next') as HTMLButtonElement
  const title = el('div', 'scheduler-title', 'title')
  const viewSwitch = el('div', 'scheduler-view-switch', 'view-switch')

  prevButton.type = 'button'
  todayButton.type = 'button'
  nextButton.type = 'button'
  prevButton.innerHTML = '&lsaquo;'
  nextButton.innerHTML = '&rsaquo;'
  viewSwitch.setAttribute('role', 'group')
  nav.append(prevButton, todayButton, nextButton)
  toolbar.append(nav, title, viewSwitch)

  const header = el('div', 'scheduler-header', 'header')
  const headerGutter = el('div', 'scheduler-gutter')
  const headerColumns = el('div', 'scheduler-header-columns')
  header.append(headerGutter, headerColumns)

  const allDayLane = el('div', 'scheduler-all-day', 'all-day-lane')
  const allDayLabel = el('div', 'scheduler-gutter scheduler-all-day-label')
  const allDayColumns = el('div', 'scheduler-all-day-columns')
  allDayLane.append(allDayLabel, allDayColumns)

  const body = el('div', 'scheduler-body', 'body')
  const timeAxis = el('div', 'scheduler-time-axis', 'time-axis')
  const grid = el('div', 'scheduler-grid', 'grid')
  grid.setAttribute('role', 'grid')
  body.append(timeAxis, grid)

  const loadingIndicator = el('div', 'scheduler-loading', 'loading')
  loadingIndicator.hidden = true
  toolbar.append(loadingIndicator)

  const liveRegion = el('div', 'visually-hidden', 'live-region')
  liveRegion.setAttribute('aria-live', 'polite')

  element.replaceChildren(toolbar, header, allDayLane, body, liveRegion)

  prevButton.addEventListener('click', () => callbacks.onNavigate('prev'))
  todayButton.addEventListener('click', () => callbacks.onNavigate('today'))
  nextButton.addEventListener('click', () => callbacks.onNavigate('next'))
  viewSwitch.addEventListener('click', event => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-coreui-view]')

    if (button) {
      callbacks.onViewSelect(button.dataset.coreuiView as SchedulerView)
    }
  })
  grid.addEventListener('click', event => {
    const more = (event.target as HTMLElement).closest<HTMLElement>('[data-part="more-link"]')

    if (more?.dataset.coreuiDate) {
      callbacks.onMoreLink?.(more.dataset.coreuiDate)
    }
  })

  // The header and all-day rows sit outside the scroll container, so a
  // horizontally scrolled (virtualized) grid drags them along via transform.
  const syncRowScroll = () => {
    const offset = body.scrollLeft
    headerColumns.style.transform = offset ? `translateX(${-offset}px)` : ''
    allDayColumns.style.transform = offset ? `translateX(${-offset}px)` : ''
  }

  body.addEventListener('scroll', syncRowScroll, { passive: true })

  const sizeColumnRow = (row: HTMLElement, ctx: RenderContext) => {
    const virtualize = isVirtualized(ctx)
    row.style.gridAutoColumns = virtualize ? `${ctx.resourceColumnWidth}px` : ''
    row.style.width =
      virtualize && ctx.viewModel ? `${ctx.viewModel.columns.length * ctx.resourceColumnWidth}px` : ''
  }

  const dragPreview = el('div', 'scheduler-drag-preview', 'drag-preview')
  const selectionOverlay = el('div', 'scheduler-selection-overlay', 'selection-overlay')

  const boxRefs = new Map<string, BoxRef>()
  const eventLayers = new Map<number, HTMLElement>()
  const monthCells: HTMLElement[] = []
  let nowIndicators: HTMLElement[] = []
  let context: RenderContext | null = null
  let lastState: InteractionState = { type: 'idle' }
  let virtualizer: Virtualizer<HTMLElement, Element> | null = null
  let unmountVirtualizer: (() => void) | null = null
  let windowFrame = 0

  const scheduleWindowRender = () => {
    if (windowFrame) {
      return
    }

    windowFrame = requestAnimationFrame(() => {
      windowFrame = 0

      if (context && isTimeGridView(context.view)) {
        renderGrid(context)
        applyPreview(lastState)
        callbacks.onWindowChange?.()
      }
    })
  }

  const ensureVirtualizer = (ctx: RenderContext): Virtualizer<HTMLElement, Element> => {
    const count = ctx.viewModel?.columns.length ?? 0

    if (!virtualizer) {
      virtualizer = new Virtualizer<HTMLElement, Element>({
        count,
        estimateSize: () => ctx.resourceColumnWidth,
        getScrollElement: () => body,
        horizontal: true,
        observeElementOffset,
        observeElementRect,
        onChange: scheduleWindowRender,
        overscan: 3,
        scrollToFn: elementScroll
      })
      unmountVirtualizer = virtualizer._didMount()
    } else if (virtualizer.options.count !== count) {
      virtualizer.setOptions({ ...virtualizer.options, count })
    }

    virtualizer._willUpdate()
    return virtualizer
  }

  const timeFormatter = (ctx: RenderContext) =>
    new Intl.DateTimeFormat(ctx.locale, { hour: '2-digit', minute: '2-digit', timeZone: ctx.timeZone })

  const visibleDays = (ctx: RenderContext): Temporal.PlainDate[] =>
    ctx.viewModel?.days ?? ctx.timelineModel?.days ?? ctx.monthModel?.days ?? ctx.dayList?.map(entry => entry.date) ?? []

  const renderToolbar = (ctx: RenderContext) => {
    const { labels } = ctx
    prevButton.setAttribute('aria-label', labels.prev)
    nextButton.setAttribute('aria-label', labels.next)
    todayButton.textContent = labels.today

    const days = visibleDays(ctx)
    const first = days[0]
    const last = days[days.length - 1]

    if (ctx.view === 'month') {
      title.textContent = new Intl.DateTimeFormat(ctx.locale, { month: 'long', year: 'numeric', timeZone: 'UTC' })
        .format(toUTCDate(first.add({ days: Math.floor(days.length / 2) })))
    } else {
      const dateFormatter = new Intl.DateTimeFormat(ctx.locale, { dateStyle: 'medium', timeZone: 'UTC' })
      title.textContent =
        days.length > 1 ?
          dateFormatter.formatRange(toUTCDate(first), toUTCDate(last)) :
          dateFormatter.format(toUTCDate(first))
    }

    const views: Array<{ view: SchedulerView; label: string }> = [
      { view: 'day', label: labels.day },
      { view: 'week', label: labels.week },
      { view: 'month', label: labels.month },
      { view: 'agenda', label: labels.agenda },
      ...(ctx.showResourceButton ?
        [
          { view: 'resource' as SchedulerView, label: labels.resource },
          { view: 'timeline' as SchedulerView, label: labels.timeline }
        ] :
        [])
    ]

    viewSwitch.setAttribute('aria-label', labels.views)
    viewSwitch.replaceChildren(
      ...views.map(({ view, label }) => {
        const button = el('button', 'scheduler-view-button', 'view-button') as HTMLButtonElement
        button.type = 'button'
        button.dataset.coreuiView = view
        button.textContent = label
        button.setAttribute('aria-pressed', String(view === ctx.view))
        return button
      })
    )
  }

  const renderHeader = (ctx: RenderContext) => {
    const weekdayFormatter = new Intl.DateTimeFormat(ctx.locale, { weekday: 'short', day: 'numeric', timeZone: 'UTC' })
    header.hidden = ctx.view === 'agenda'
    headerGutter.hidden = ctx.view === 'month'
    headerGutter.style.flexBasis = ctx.view === 'timeline' ? 'var(--cui-scheduler-timeline-label-width)' : ''
    sizeColumnRow(headerColumns, ctx)

    if (ctx.view === 'timeline') {
      const dayFormatter = new Intl.DateTimeFormat(ctx.locale, {
 weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'
})
      const dayWidth = 24 * ctx.timelineHourWidth
      headerColumns.style.gridAutoColumns = `${dayWidth}px`
      headerColumns.style.width = `${(ctx.timelineModel?.days.length ?? 0) * dayWidth}px`
      headerColumns.replaceChildren(
        ...(ctx.timelineModel?.days ?? []).map(day => {
          const cell = el('div', 'scheduler-column-header', 'column-header')
          cell.textContent = dayFormatter.format(toUTCDate(day))
          return cell
        })
      )
      return
    }

    if (ctx.view === 'month') {
      const nameFormatter = new Intl.DateTimeFormat(ctx.locale, { weekday: 'short', timeZone: 'UTC' })
      const days = visibleDays(ctx).slice(0, 7)
      headerColumns.replaceChildren(
        ...days.map(day => {
          const cell = el('div', 'scheduler-column-header', 'column-header')
          cell.textContent = nameFormatter.format(toUTCDate(day))
          return cell
        })
      )
      return
    }

    headerColumns.replaceChildren(
      ...(ctx.viewModel?.columns ?? []).map(column => {
        const cell = el('div', 'scheduler-column-header', 'column-header')

        cell.textContent = column.resourceId === undefined ? weekdayFormatter.format(toUTCDate(column.date)) : column.label ?? column.resourceId

        return cell
      })
    )
  }

  const renderAllDay = (ctx: RenderContext) => {
    allDayLabel.textContent = ctx.labels.allDay
    sizeColumnRow(allDayColumns, ctx)

    if (!isTimeGridView(ctx.view) || !ctx.viewModel) {
      allDayLane.hidden = true
      return
    }

    allDayColumns.replaceChildren(
      ...ctx.viewModel.columnLayouts.map(columnLayout => {
        const column = el('div', 'scheduler-all-day-column')

        column.append(
          ...columnLayout.allDay.map((occurrence, chipIndex) => {
            const chip = buildEventElement(ctx, occurrence, `${occurrence.key}#ad${chipIndex}`, ctx.labels.allDay)
            chip.classList.add('scheduler-event-all-day')
            const chipTitle = el('div', 'scheduler-event-title', 'event-title')
            chipTitle.textContent = occurrence.event.title ?? ''
            chip.append(chipTitle)
            return chip
          })
        )

        return column
      })
    )

    allDayLane.hidden = ctx.viewModel.columnLayouts.every(columnLayout => columnLayout.allDay.length === 0)
  }

  const renderTimeAxis = (ctx: RenderContext) => {
    timeAxis.hidden = !isTimeGridView(ctx.view)

    if (timeAxis.hidden) {
      return
    }

    const hours = []

    for (let hour = ctx.dayStartHour; hour < ctx.dayEndHour; hour++) {
      hours.push(hour)
    }

    timeAxis.replaceChildren(
      ...hours.map(hour => {
        const cell = el('div', 'scheduler-time-label')
        cell.style.height = `${ctx.hourHeight}px`
        const label = el('span', 'scheduler-time-label-text')
        label.textContent = formatMinutes(hour * 60)
        cell.append(label)
        return cell
      })
    )
  }

  const applyColor = (target: HTMLElement, occurrence: EventOccurrence, ctx: RenderContext) => {
    const color = eventColor(occurrence, ctx.resources)

    if (color) {
      target.style.setProperty('--cui-scheduler-event-bg', color)
    }
  }

  const buildEventElement = (
    ctx: RenderContext,
    occurrence: EventOccurrence,
    boxKey: string,
    timeText: string
  ): HTMLElement => {
    const eventEl = el('div', 'scheduler-event', 'event')
    eventEl.dataset.coreuiKey = occurrence.key
    eventEl.dataset.coreuiBoxKey = boxKey
    eventEl.tabIndex = 0
    eventEl.classList.toggle('scheduler-event-selected', ctx.selection.includes(occurrence.key))
    eventEl.setAttribute('aria-label', `${occurrence.event.title ?? ''}, ${timeText}`)
    applyColor(eventEl, occurrence, ctx)

    if (ctx.selection.includes(occurrence.key)) {
      eventEl.setAttribute('aria-selected', 'true')
    }

    return eventEl
  }

  const renderGrid = (ctx: RenderContext) => {
    grid.className = 'scheduler-grid'
    monthCells.length = 0
    boxRefs.clear()
    eventLayers.clear()
    nowIndicators = []

    if (ctx.view === 'month') {
      renderMonth(ctx)
      return
    }

    if (ctx.view === 'agenda') {
      renderAgenda(ctx)
      return
    }

    if (ctx.view === 'timeline') {
      renderTimeline(ctx)
      return
    }

    renderTimeGrid(ctx)
  }

  const renderTimeline = (ctx: RenderContext) => {
    const model = ctx.timelineModel

    if (!model) {
      return
    }

    grid.className = 'scheduler-grid scheduler-timeline'
    grid.style.width = ''
    grid.removeAttribute('aria-colcount')
    grid.setAttribute('aria-rowcount', String(model.rows.length))
    grid.style.setProperty('--cui-scheduler-timeline-hour-w', `${ctx.timelineHourWidth}px`)
    const ppm = ctx.timelineHourWidth / 60
    const formatter = timeFormatter(ctx)
    const canvasWidth = model.totalMinutes * ppm

    grid.replaceChildren(
      ...model.rows.map((row, rowIndex) => {
        const rowEl = el('div', 'scheduler-timeline-row', 'timeline-row')
        rowEl.dataset.coreuiRow = String(rowIndex)
        rowEl.setAttribute('role', 'row')

        const label = el('div', 'scheduler-timeline-label', 'resource-header')
        label.setAttribute('role', 'rowheader')
        label.textContent = row.resource.label ?? row.resource.id

        const canvas = el('div', 'scheduler-timeline-canvas', 'timeline-canvas')
        canvas.style.width = `${canvasWidth}px`
        canvas.style.height = `${Math.max(row.laneCount, 1) * TIMELINE_LANE_HEIGHT}px`
        eventLayers.set(rowIndex, canvas)

        for (const box of row.boxes) {
          const { occurrence } = box
          const timeText = occurrence.allDay ?
            ctx.labels.allDay :
            `${formatter.format(new Date(occurrence.start.epochMilliseconds))} – ${formatter.format(new Date(occurrence.end.epochMilliseconds))}`
          const bar = buildEventElement(ctx, occurrence, box.key, timeText)
          bar.classList.add('scheduler-timeline-bar')
          bar.classList.toggle('scheduler-event-continues-before', box.continuesBefore)
          bar.classList.toggle('scheduler-event-continues-after', box.continuesAfter)
          bar.style.left = `${box.startMinutes * ppm}px`
          bar.style.width = `${Math.max(((box.endMinutes - box.startMinutes) * ppm) - 2, 6)}px`
          bar.style.top = `${(box.track * TIMELINE_LANE_HEIGHT) + 2}px`
          bar.style.height = `${TIMELINE_LANE_HEIGHT - 4}px`

          const startHandle = el('div', 'scheduler-event-resize scheduler-event-resize-start', 'resize-handle-start')
          const barTitle = el('span', 'scheduler-event-title', 'event-title')
          barTitle.textContent = occurrence.event.title ?? ''
          const endHandle = el('div', 'scheduler-event-resize scheduler-event-resize-end', 'resize-handle-end')
          bar.append(startHandle, barTitle, endHandle)

          canvas.append(bar)
          boxRefs.set(box.key, { box, columnIndex: rowIndex, element: bar })
        }

        if (ctx.now) {
          const indicator = el('div', 'scheduler-now-indicator scheduler-timeline-now', 'now-indicator')
          canvas.append(indicator)
          nowIndicators.push(indicator)
        }

        rowEl.append(label, canvas)
        return rowEl
      })
    )

    updateNow(ctx.now)
  }

  const renderTimeGrid = (ctx: RenderContext) => {
    if (!ctx.viewModel) {
      return
    }

    const ppm = ctx.hourHeight / 60
    const startMinutes = ctx.dayStartHour * 60
    const endMinutes = ctx.dayEndHour * 60
    const formatter = timeFormatter(ctx)
    const weekdayFormatter = new Intl.DateTimeFormat(ctx.locale, {
 weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC'
})

    const virtualize = isVirtualized(ctx)
    grid.classList.toggle('scheduler-grid-virtualized', virtualize)
    grid.setAttribute('aria-colcount', String(ctx.viewModel.columns.length))
    syncRowScroll()

    let columnIndexes = ctx.viewModel.columnLayouts.map((_, index) => index)

    if (virtualize) {
      const instance = ensureVirtualizer(ctx)
      grid.style.width = `${instance.getTotalSize()}px`
      columnIndexes = instance.getVirtualItems().map(item => item.index)
    } else {
      grid.style.width = ''
    }

    grid.replaceChildren(
      ...columnIndexes.map(columnIndex => {
        const columnLayout = (ctx.viewModel as SchedulerViewModel).columnLayouts[columnIndex]
        const { date, label, resourceId } = columnLayout.column
        const columnLabel =
          resourceId === undefined ? weekdayFormatter.format(toUTCDate(date)) : label ?? resourceId

        const column = el('div', 'scheduler-column', 'column')
        column.dataset.coreuiColumn = String(columnIndex)
        column.setAttribute('role', 'row')
        column.setAttribute('aria-label', columnLabel)
        column.setAttribute('aria-colindex', String(columnIndex + 1))

        if (virtualize) {
          column.style.left = `${columnIndex * ctx.resourceColumnWidth}px`
          column.style.width = `${ctx.resourceColumnWidth}px`
        }

        for (let hour = ctx.dayStartHour; hour < ctx.dayEndHour; hour++) {
          const slot = el('div', 'scheduler-time-slot', 'time-slot')
          slot.dataset.coreuiMinutes = String(hour * 60)
          slot.style.height = `${ctx.hourHeight}px`
          slot.setAttribute('role', 'gridcell')
          slot.setAttribute('aria-label', `${columnLabel}, ${formatMinutes(hour * 60)}`)
          slot.tabIndex = -1
          column.append(slot)
        }

        appendNonWorking(column, columnLayout.column.date, ctx, ppm, startMinutes, endMinutes)

        const events = el('div', 'scheduler-column-events')
        eventLayers.set(columnIndex, events)

        for (const box of columnLayout.boxes) {
          const { occurrence } = box
          const timeRange = `${formatter.format(new Date(occurrence.start.epochMilliseconds))} – ${formatter.format(new Date(occurrence.end.epochMilliseconds))}`
          const eventEl = buildEventElement(ctx, occurrence, box.key, timeRange)
          eventEl.style.top = `${(box.top - startMinutes) * ppm}px`
          eventEl.style.height = `${box.height * ppm}px`
          eventEl.style.left = `${box.leftPct}%`
          eventEl.style.width = `calc(${box.widthPct}% - 2px)`
          eventEl.classList.toggle('scheduler-event-continues-before', box.continuesBefore)
          eventEl.classList.toggle('scheduler-event-continues-after', box.continuesAfter)

          const startHandle = el('div', 'scheduler-event-resize scheduler-event-resize-start', 'resize-handle-start')
          const time = el('div', 'scheduler-event-time')
          time.textContent = timeRange
          const eventTitle = el('div', 'scheduler-event-title', 'event-title')
          eventTitle.textContent = occurrence.event.title ?? ''
          const endHandle = el('div', 'scheduler-event-resize scheduler-event-resize-end', 'resize-handle-end')
          eventEl.append(startHandle, time, eventTitle, endHandle)

          events.append(eventEl)
          boxRefs.set(box.key, { box, columnIndex, element: eventEl })
        }

        if (ctx.now && columnLayout.column.date.equals(ctx.now.toPlainDate())) {
          const indicator = el('div', 'scheduler-now-indicator', 'now-indicator')
          events.append(indicator)
          nowIndicators.push(indicator)
        }

        column.append(events)
        return column
      })
    )

    updateNow(ctx.now)
  }

  const appendNonWorking = (
    column: HTMLElement,
    date: Temporal.PlainDate,
    ctx: RenderContext,
    ppm: number,
    startMinutes: number,
    endMinutes: number
  ) => {
    if (!ctx.businessHours) {
      return
    }

    const { daysOfWeek, startHour, endHour } = ctx.businessHours
    const addBand = (from: number, to: number) => {
      if (to <= from) {
        return
      }

      const band = el('div', 'scheduler-non-working', 'non-working')
      band.style.top = `${(from - startMinutes) * ppm}px`
      band.style.height = `${(to - from) * ppm}px`
      column.append(band)
    }

    if (daysOfWeek && !daysOfWeek.includes(date.dayOfWeek)) {
      addBand(startMinutes, endMinutes)
      return
    }

    addBand(startMinutes, Math.min(startHour * 60, endMinutes))
    addBand(Math.max(endHour * 60, startMinutes), endMinutes)
  }

  const updateNow = (now: Temporal.ZonedDateTime | null) => {
    if (!context || !now || nowIndicators.length === 0) {
      return
    }

    if (context.view === 'timeline') {
      const model = context.timelineModel

      if (!model) {
        return
      }

      const ppm = context.timelineHourWidth / 60
      const dayOffset = model.days[0].until(now.toPlainDate()).total({ unit: 'days' })
      const offset = (dayOffset * MINUTES_IN_DAY) + (now.hour * 60) + now.minute
      const visible = offset >= 0 && offset <= model.totalMinutes

      for (const indicator of nowIndicators) {
        indicator.hidden = !visible
        indicator.style.left = `${offset * ppm}px`
      }

      return
    }

    const ppm = context.hourHeight / 60
    const minutes = (now.hour * 60) + now.minute
    const startMinutes = context.dayStartHour * 60
    const visible = minutes >= startMinutes && minutes <= context.dayEndHour * 60

    for (const indicator of nowIndicators) {
      indicator.hidden = !visible
      indicator.style.top = `${(minutes - startMinutes) * ppm}px`
    }
  }

  const renderMonth = (ctx: RenderContext) => {
    const model = ctx.monthModel

    if (!model) {
      return
    }

    grid.className = 'scheduler-grid scheduler-month-grid'
    grid.style.width = ''
    grid.setAttribute('aria-colcount', '7')
    grid.setAttribute('aria-rowcount', String(model.weeks.length))
    const formatter = timeFormatter(ctx)
    const today = ctx.now?.toPlainDate()
    const anchorMonth = model.days[Math.floor(model.days.length / 2)]?.month

    const buildCell = (entry: DayOccurrences, dayIndex: number, laneCount: number): HTMLElement => {
      const cell = el('div', 'scheduler-month-day', 'month-day')
      cell.dataset.coreuiDayIndex = String(dayIndex)
      cell.dataset.coreuiDate = entry.dateISO
      cell.setAttribute('role', 'gridcell')
      cell.classList.toggle('scheduler-month-day-outside', entry.date.month !== anchorMonth)
      monthCells.push(cell)

      if (today && entry.date.equals(today)) {
        cell.classList.add('scheduler-month-day-today')
      }

      const number = el('div', 'scheduler-month-day-number')
      number.textContent = String(entry.date.day)
      cell.append(number)

      if (laneCount > 0) {
        const spacer = el('div', 'scheduler-month-bar-spacer')
        spacer.style.height = `${laneCount * MONTH_BAR_HEIGHT}px`
        cell.append(spacer)
      }

      const visible = entry.occurrences.slice(0, ctx.monthMaxEventsPerDay)
      const hidden = entry.occurrences.length - visible.length

      for (const occurrence of visible) {
        const startsHere = occurrence.allDay || occurrence.start.toPlainDate().equals(entry.date)
        const timeText = occurrence.allDay ?
          ctx.labels.allDay :
          formatter.format(new Date(occurrence.start.epochMilliseconds))
        const chip = buildEventElement(ctx, occurrence, `${occurrence.key}#m${dayIndex}`, timeText)
        chip.classList.add('scheduler-event-chip')
        chip.dataset.coreuiDayIndex = String(dayIndex)

        const chipTitle = el('span', 'scheduler-event-title', 'event-title')
        chipTitle.textContent = occurrence.event.title ?? ''

        if (!occurrence.allDay && startsHere) {
          const time = el('span', 'scheduler-event-time')
          time.textContent = formatter.format(new Date(occurrence.start.epochMilliseconds))
          chip.append(time)
        }

        chip.append(chipTitle)
        cell.append(chip)
      }

      if (hidden > 0) {
        const more = el('button', 'scheduler-more-link', 'more-link') as HTMLButtonElement
        more.type = 'button'
        more.dataset.coreuiDate = entry.dateISO
        more.textContent = ctx.labels.more.replace('{count}', String(hidden))
        cell.append(more)
      }

      return cell
    }

    const weekRows: HTMLElement[] = []

    for (const [weekIndex, week] of model.weeks.entries()) {
      const weekStart = weekIndex * 7
      const weekEl = el('div', 'scheduler-month-week')
      weekEl.setAttribute('role', 'row')
      weekEl.setAttribute('aria-rowindex', String(weekIndex + 1))

      weekEl.append(...week.days.map((entry, index) => {
        const cell = buildCell(entry, weekStart + index, week.laneCount)
        cell.setAttribute('aria-colindex', String(index + 1))
        return cell
      }))

      for (const bar of week.bars) {
        const timeText = bar.occurrence.allDay ?
          ctx.labels.allDay :
          formatter.format(new Date(bar.occurrence.start.epochMilliseconds))
        const barEl = buildEventElement(ctx, bar.occurrence, bar.key, timeText)
        barEl.classList.add('scheduler-month-bar')
        barEl.classList.toggle('scheduler-event-continues-before', bar.continuesBefore)
        barEl.classList.toggle('scheduler-event-continues-after', bar.continuesAfter)
        barEl.dataset.coreuiDayIndex = String(weekStart + bar.startCol)
        barEl.style.left = `calc(${(bar.startCol / 7) * 100}% + 2px)`
        barEl.style.width = `calc(${((bar.endCol - bar.startCol + 1) / 7) * 100}% - 4px)`
        barEl.style.top = `${(bar.lane * MONTH_BAR_HEIGHT) + 26}px`

        const barTitle = el('span', 'scheduler-event-title', 'event-title')
        barTitle.textContent = bar.occurrence.event.title ?? ''
        barEl.append(barTitle)
        weekEl.append(barEl)
      }

      weekRows.push(weekEl)
    }

    grid.replaceChildren(...weekRows)
  }

  const renderAgenda = (ctx: RenderContext) => {
    const dayList = (ctx.dayList ?? []).filter(entry => entry.occurrences.length > 0)
    grid.className = 'scheduler-grid scheduler-agenda'
    grid.style.width = ''
    grid.removeAttribute('aria-colcount')

    if (dayList.length === 0) {
      const empty = el('div', 'scheduler-agenda-empty', 'agenda-empty')
      empty.textContent = ctx.labels.agendaEmpty
      grid.replaceChildren(empty)
      return
    }

    const formatter = timeFormatter(ctx)
    const dayFormatter = new Intl.DateTimeFormat(ctx.locale, {
 weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC'
})

    grid.replaceChildren(
      ...dayList.map(entry => {
        const section = el('div', 'scheduler-agenda-day', 'agenda-day')
        const heading = el('div', 'scheduler-agenda-day-header')
        heading.textContent = dayFormatter.format(toUTCDate(entry.date))
        section.append(heading)

        for (const occurrence of entry.occurrences) {
          const timeText = occurrence.allDay ?
            ctx.labels.allDay :
            `${formatter.format(new Date(occurrence.start.epochMilliseconds))} – ${formatter.format(new Date(occurrence.end.epochMilliseconds))}`
          const row = buildEventElement(ctx, occurrence, `${occurrence.key}#a${entry.dateISO}`, timeText)
          row.classList.add('scheduler-agenda-event')

          const time = el('span', 'scheduler-event-time')
          time.textContent = timeText
          const rowTitle = el('span', 'scheduler-event-title', 'event-title')
          rowTitle.textContent = occurrence.event.title ?? ''
          row.append(time, rowTitle)
          section.append(row)
        }

        return section
      })
    )
  }

  const dialogHost = el('div', 'scheduler-dialog-backdrop', 'event-dialog')

  // aria-modal promises focus containment; keep Tab cycling inside the form.
  dialogHost.addEventListener('keydown', event => {
    if (event.key !== 'Tab') {
      return
    }

    const focusables = [...dialogHost.querySelectorAll<HTMLElement>('input, select, button')]

    if (focusables.length === 0) {
      return
    }

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  })

  const field = (labelText: string, input: HTMLElement): HTMLElement => {
    const wrap = el('label', 'scheduler-dialog-field')
    const caption = el('span', 'scheduler-dialog-label')
    caption.textContent = labelText
    wrap.append(caption, input)
    return wrap
  }

  const selectField = (part: string, labelText: string, options: Array<[string, string]>, value: string): HTMLElement => {
    const select = el('select', 'scheduler-popover-select', part) as HTMLSelectElement
    const known = options.some(([optionValue]) => optionValue === value)

    for (const [optionValue, text] of known ? options : [...options, [value, value] as [string, string]]) {
      const option = document.createElement('option')
      option.value = optionValue
      option.textContent = text
      select.append(option)
    }

    select.value = value
    return field(labelText, select)
  }

  const renderDialog = (ctx: RenderContext) => {
    const occurrence = ctx.dialogOccurrence

    if (!occurrence) {
      dialogHost.remove()
      return
    }

    // Keep the user's in-progress edits: only rebuild when the dialog opens
    // or switches to another occurrence.
    if (dialogHost.isConnected && dialogHost.dataset.coreuiKey === occurrence.key) {
      return
    }

    const { labels } = ctx
    dialogHost.replaceChildren()
    dialogHost.setAttribute('role', 'dialog')
    dialogHost.setAttribute('aria-modal', 'true')
    dialogHost.setAttribute('aria-label', labels.editEvent)
    dialogHost.dataset.coreuiKey = occurrence.key

    const card = el('form', 'scheduler-dialog')

    const heading = el('div', 'scheduler-dialog-title')
    heading.textContent = labels.editEvent
    card.append(heading)

    const titleInput = el('input', 'scheduler-dialog-input', 'title-input') as HTMLInputElement
    titleInput.type = 'text'
    titleInput.value = occurrence.event.title ?? ''
    card.append(field(labels.titleLabel, titleInput))

    const startInput = el('input', 'scheduler-dialog-input', 'start-input') as HTMLInputElement
    const endInput = el('input', 'scheduler-dialog-input', 'end-input') as HTMLInputElement

    if (occurrence.allDay) {
      startInput.type = 'date'
      startInput.value = occurrence.start.toPlainDate().toString()
      endInput.type = 'date'
      endInput.value = occurrence.end.toPlainDate().toString()
    } else {
      startInput.type = 'datetime-local'
      startInput.value = localDateTimeValue(occurrence.start)
      endInput.type = 'datetime-local'
      endInput.value = localDateTimeValue(occurrence.end)
    }

    card.append(field(labels.startLabel, startInput), field(labels.endLabel, endInput))

    const colorInput = el('input', 'scheduler-dialog-color', 'color-input') as HTMLInputElement
    colorInput.type = 'color'
    colorInput.value = /^#[0-9a-f]{6}$/i.test(occurrence.event.color ?? '') ? (occurrence.event.color as string) : '#321fdb'
    colorInput.dataset.coreuiInitial = colorInput.value
    card.append(field(labels.colorLabel, colorInput))

    card.append(
      selectField('repeat-select', labels.repeat, [
        ['', labels.repeatNone],
        ['FREQ=DAILY', labels.repeatDaily],
        ['FREQ=WEEKLY', labels.repeatWeekly],
        ['FREQ=MONTHLY', labels.repeatMonthly]
      ], occurrence.event.rrule?.replace(/^RRULE:/i, '') ?? '')
    )

    if (occurrence.recurring) {
      card.append(
        selectField('scope-select', labels.editScope, [
          ['occurrence', labels.scopeOccurrence],
          ['future', labels.scopeFuture],
          ['all', labels.scopeAll]
        ], ctx.recurringEditScope)
      )
    }

    const footer = el('div', 'scheduler-dialog-footer')
    const deleteButton = el('button', 'scheduler-popover-delete', 'delete-button') as HTMLButtonElement
    deleteButton.type = 'button'
    deleteButton.textContent = labels.deleteEvent
    const cancelButton = el('button', 'scheduler-view-button', 'dialog-cancel') as HTMLButtonElement
    cancelButton.type = 'button'
    cancelButton.textContent = labels.cancel
    const saveButton = el('button', 'scheduler-dialog-save', 'dialog-save') as HTMLButtonElement
    saveButton.type = 'submit'
    saveButton.textContent = labels.save
    footer.append(deleteButton, cancelButton, saveButton)
    card.append(footer)

    dialogHost.append(card)
    element.append(dialogHost)
  }

  const contextOccurrence = (key: string | undefined): EventOccurrence | undefined => {
    if (!key || !context) {
      return undefined
    }

    const occurrences =
      context.viewModel?.occurrences ?? context.monthModel?.occurrences ?? context.timelineModel?.occurrences ?? []
    return occurrences.find(occurrence => occurrence.key === key)
  }

  const markDragSource = (key: string | undefined) => {
    for (const eventEl of element.querySelectorAll<HTMLElement>('[data-part="event"].scheduler-event-dragging')) {
      eventEl.classList.remove('scheduler-event-dragging')
    }

    if (!key) {
      return
    }

    for (const eventEl of element.querySelectorAll<HTMLElement>('[data-part="event"]')) {
      if (eventEl.dataset.coreuiKey === key) {
        eventEl.classList.add('scheduler-event-dragging')
      }
    }
  }

  const fillGhost = (target: HTMLElement, occurrence: EventOccurrence | undefined, timeText: string) => {
    target.replaceChildren()

    if (timeText) {
      const time = el('span', 'scheduler-event-time')
      time.textContent = timeText
      target.append(time)
    }

    const ghostTitle = el('span', 'scheduler-event-title')
    ghostTitle.textContent = occurrence?.event.title ?? ''
    target.append(ghostTitle)

    const color = occurrence ? eventColor(occurrence, context?.resources ?? []) : undefined

    if (color) {
      target.style.setProperty('--cui-scheduler-event-bg', color)
    } else {
      target.style.removeProperty('--cui-scheduler-event-bg')
    }
  }

  const applyPreview = (state: InteractionState) => {
    if (!context) {
      return
    }

    lastState = state
    markDragSource(state.type === 'drag-move' || state.type === 'drag-resize' ? state.occurrenceKey : undefined)
    const ppm = context.hourHeight / 60
    const startMinutes = context.dayStartHour * 60
    dragPreview.remove()
    selectionOverlay.remove()
    element.classList.toggle('scheduler-interacting', state.type === 'drag-move' || state.type === 'drag-resize' || state.type === 'select-range')

    if (context.view === 'month') {
      applyMonthPreview(state)
      return
    }

    if (context.view === 'timeline') {
      const ppmH = context.timelineHourWidth / 60

      if (state.type === 'drag-move' || state.type === 'drag-resize') {
        const layer = eventLayers.get(state.preview.column)

        if (layer) {
          dragPreview.style.top = '2px'
          dragPreview.style.height = `${TIMELINE_LANE_HEIGHT - 4}px`
          dragPreview.style.insetInline = ''
          dragPreview.style.left = `${state.preview.startMinutes * ppmH}px`
          dragPreview.style.width = `${(state.preview.endMinutes - state.preview.startMinutes) * ppmH}px`
          fillGhost(dragPreview, contextOccurrence(state.occurrenceKey), '')
          layer.append(dragPreview)
        }
      } else if (state.type === 'select-range') {
        const layer = eventLayers.get(state.range.startColumn)

        if (layer) {
          selectionOverlay.style.insetInline = ''
          selectionOverlay.style.top = '0'
          selectionOverlay.style.height = '100%'
          selectionOverlay.style.left = `${state.range.startMinutes * ppmH}px`
          selectionOverlay.style.width = `${(state.range.endMinutes - state.range.startMinutes) * ppmH}px`
          layer.append(selectionOverlay)
        }
      }

      return
    }

    if (state.type === 'drag-move' || state.type === 'drag-resize') {
      const layer = eventLayers.get(state.preview.column)

      if (!layer) {
        return
      }

      dragPreview.style.left = ''
      dragPreview.style.width = ''
      dragPreview.style.top = `${(state.preview.startMinutes - startMinutes) * ppm}px`
      dragPreview.style.height = `${(state.preview.endMinutes - state.preview.startMinutes) * ppm}px`
      fillGhost(
        dragPreview,
        contextOccurrence(state.occurrenceKey),
        `${formatMinutes(state.preview.startMinutes)} – ${formatMinutes(state.preview.endMinutes)}`
      )
      layer.append(dragPreview)
      return
    }

    if (state.type === 'select-range') {
      const layer = eventLayers.get(state.range.startColumn)

      if (!layer) {
        return
      }

      const sameColumn = state.range.startColumn === state.range.endColumn
      const endMinutes = sameColumn ? state.range.endMinutes : context.dayEndHour * 60
      selectionOverlay.style.left = ''
      selectionOverlay.style.width = ''
      selectionOverlay.style.top = `${(state.range.startMinutes - startMinutes) * ppm}px`
      selectionOverlay.style.height = `${(endMinutes - state.range.startMinutes) * ppm}px`
      layer.append(selectionOverlay)
    }
  }

  const monthGhost = el('div', 'scheduler-event scheduler-month-ghost')

  const applyMonthPreview = (state: InteractionState) => {
    for (const cell of monthCells) {
      cell.classList.remove('scheduler-month-day-target')
    }

    monthGhost.remove()

    if (state.type === 'drag-move') {
      const occurrence = contextOccurrence(state.occurrenceKey)
      const spanDays = occurrence ?
        Math.max(
          occurrence.allDay ?
            occurrence.start.toPlainDate().until(occurrence.end.toPlainDate()).total({ unit: 'days' }) :
            occurrence.start.toPlainDate().until(occurrence.end.subtract({ minutes: 1 }).toPlainDate()).total({ unit: 'days' }) + 1,
          1
        ) :
        1
      const target = state.preview.column

      for (let index = target; index < target + spanDays; index++) {
        monthCells[index]?.classList.add('scheduler-month-day-target')
      }

      const cell = monthCells[target]

      if (cell && occurrence) {
        const startCol = target % 7
        const span = Math.min(spanDays, 7 - startCol)
        fillGhost(monthGhost, occurrence, '')
        monthGhost.classList.toggle('scheduler-month-ghost-bar', spanDays > 1)

        if (spanDays > 1) {
          const week = cell.closest('[role="row"]') as HTMLElement | null
          monthGhost.style.left = `calc(${(startCol / 7) * 100}% + 2px)`
          monthGhost.style.width = `calc(${(span / 7) * 100}% - 4px)`
          week?.append(monthGhost)
        } else {
          monthGhost.style.left = ''
          monthGhost.style.width = ''
          cell.append(monthGhost)
        }
      }

      return
    }

    if (state.type === 'select-range') {
      for (let index = state.range.startColumn; index <= state.range.endColumn; index++) {
        monthCells[index]?.classList.add('scheduler-month-day-target')
      }
    }
  }

  return {
    refs: {
 body, grid, liveRegion, timeAxis, title
},

    render(nextContext: RenderContext) {
      context = nextContext
      element.dataset.coreuiView = nextContext.view
      renderToolbar(nextContext)
      renderHeader(nextContext)
      renderAllDay(nextContext)
      renderTimeAxis(nextContext)
      renderGrid(nextContext)
      renderDialog(nextContext)
      applyPreview({ type: 'idle' })
    },

    getBox(key: string): BoxRef | undefined {
      return boxRefs.get(key)
    },

    findBoxByOccurrence(occurrenceKey: string): BoxRef | undefined {
      for (const ref of boxRefs.values()) {
        if (ref.box.occurrence.key === occurrenceKey) {
          return ref
        }
      }

      return undefined
    },

    focusEvent(occurrenceKey: string) {
      const ref = this.findBoxByOccurrence(occurrenceKey)

      if (ref) {
        ref.element.focus()
        return
      }

      for (const chip of element.querySelectorAll<HTMLElement>('[data-part="event"]')) {
        if (chip.dataset.coreuiKey === occurrenceKey) {
          chip.focus()
          return
        }
      }
    },

    preview(state: InteractionState) {
      applyPreview(state)
    },

    focusDialog() {
      dialogHost.querySelector<HTMLElement>('[data-part="title-input"]')?.focus()
    },

    updateNow(now: Temporal.ZonedDateTime | null) {
      updateNow(now)
    },

    announce(message: string) {
      liveRegion.textContent = ''
      liveRegion.textContent = message
    },

    setLoading(loading: boolean) {
      loadingIndicator.hidden = !loading
      element.classList.toggle('scheduler-is-loading', loading)
    },

    dispose() {
      if (windowFrame) {
        cancelAnimationFrame(windowFrame)
        windowFrame = 0
      }

      unmountVirtualizer?.()
      virtualizer = null
    }
  }
}

export type Renderer = ReturnType<typeof createRenderer>
